import { teeupUserStatusKeys } from "@configs/enums"
import { timeFromNow } from "@utils/dateUtils"

// PEOPLE-ROW-PILLS 2026-07-05 (QJ): single source of truth for "who ranks where" so the
// collapsed People-row avatar strip (TitleContent) and the expanded per-person activity list
// (Content/Participants) provably show the SAME order -- previously each duplicated its own
// copy of this logic, which is how the two could silently drift apart.

const activityValue = p => {
  const t = p?.lastTeeupActivityCreatedAt
  const ms = t ? new Date(t).getTime() : NaN
  return Number.isFinite(ms) ? ms : null
}

const joinValue = p => {
  const t = p?.joinedAt
  const ms = t ? new Date(t).getTime() : NaN
  return Number.isFinite(ms) ? ms : Number.MAX_SAFE_INTEGER
}

// Most-recent-activity-first within a group; participants with no recorded activity sort last
// within their group, ordered by join order (earliest joiner first) as the deterministic
// fallback QJ specified.
export const sortByRecency = arr =>
  [...arr].sort((a, b) => {
    const av = activityValue(a)
    const bv = activityValue(b)
    if (av !== null && bv !== null) return bv - av
    if (av !== null) return -1
    if (bv !== null) return 1
    return joinValue(a) - joinValue(b)
  })

// QJ DEFINITIVE SPEC 2026-07-04/07-05: (1) Organizer(s) first; (2) then people GOING; (3) then
// everyone else -- WITHIN each group, most-recent-activity-first, falling back to join order.
export const orderPeopleForDisplay = people => {
  if (!people?.length) return []
  const organizers = people.filter(p => p.isOrganiser)
  const nonOrganizers = people.filter(p => !p.isOrganiser)
  const going = nonOrganizers.filter(p => p.status === teeupUserStatusKeys.going)
  const rest = nonOrganizers.filter(p => p.status !== teeupUserStatusKeys.going)
  return [...sortByRecency(organizers), ...sortByRecency(going), ...sortByRecency(rest)]
}

// EPOCH-GUARD (QJ bug report 2026-07-05, "Quentin 56y"): some activity/message records carry a
// real, parseable, but SENTINEL timestamp (epoch-zero era, e.g. `1970-01-01T00:00:00.000Z`)
// instead of a genuinely null/missing one -- `timeFromNow` only guards falsy/invalid input, so
// a sentinel epoch date passes through as a "real" ~56-year-old activity. Honest-signal law:
// never render a garbage age. Anything dated before this guard year is treated as NO time,
// same as if the field were missing outright.
const EPOCH_GUARD_YEAR = 2000

export const safeRelativeTime = timestamp => {
  if (!timestamp) return ""
  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) return ""
  if (date.getFullYear() < EPOCH_GUARD_YEAR) return ""
  return timeFromNow(timestamp)
}

// Same epoch-guard, but returning a raw ms value (or null) for SORTING per-person flow entries
// chronologically without printing a time string.
export const safeTimeValue = timestamp => {
  if (!timestamp) return null
  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) return null
  if (date.getFullYear() < EPOCH_GUARD_YEAR) return null
  return date.getTime()
}
